import { recordSkillEvent } from "@/lib/skillLedger";
// Per-outcome mastery tracker.
//
// Storage: `we_mastery_v1` = {
//   [outcomeCode]: { attempts: [{ score, dateISO }], mastered: bool }
// }
//
// Mastery rule (agreed with product): a child has met an outcome when
// ANY 3 attempts have score >= 4/5 (80%). Retry-friendly by design —
// there is no punishment for a bad first attempt; kids simply try again.

const KEY = "we_mastery_v1";
const PASS_THRESHOLD = 4;   // out of 5
const REQUIRED_PASSES = 3;

function readRaw() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeRaw(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("we-mastery-changed"));
  } catch { /* quota */ }
}

/** Full mastery table. */
export function loadMastery() {
  return readRaw();
}

/** Read one outcome's mastery record (or empty defaults). */
export function getOutcomeMastery(code) {
  const all = readRaw();
  return all[code] || { attempts: [], mastered: false };
}

/** Record a new attempt for a code and return the updated record.
 *  Feb 2026 · Bug #11 fix — cross-wire into the progress store so
 *  the Parent Dashboard's Stars / Quiz count / "First Quiz Done"
 *  badge trigger reliably whenever a child passes a mastery test. */
export function recordAttempt(code, score, total = 5) {
  try { recordSkillEvent({ skillId: `outcome:${code}`, correct: score >= PASS_THRESHOLD, kind: "check", skillType: "outcome", meta: { score, total } }); } catch { /* best-effort */ }
  const all = readRaw();
  const cur = all[code] || { attempts: [], mastered: false };
  const attempts = [...cur.attempts, {
    score,
    total,
    dateISO: new Date().toISOString(),
  }].slice(-10); // cap history at 10 to keep storage tight
  const passes = attempts.filter((a) => a.score >= PASS_THRESHOLD).length;
  const wasMastered = !!cur.mastered;
  const mastered = wasMastered || passes >= REQUIRED_PASSES;
  const next = { ...all, [code]: { attempts, mastered } };
  writeRaw(next);
  try {
    // Every attempt at PASS_THRESHOLD or above should look like a
    // completed quiz on the Parent Dashboard. Fire once per attempt
    // — the progress store dedupes stars/counters internally.
    if (score >= PASS_THRESHOLD) {
      // Late-bound require to avoid a top-level circular during
      // module init when /lib/progress transitively depends on us.
      // eslint-disable-next-line global-require
      const p = require("@/lib/progress");
      if (typeof p.recordEvent === "function") {
        p.recordEvent("quiz", `Passed mastery: ${code}`);
      }
      if (typeof p.addStars === "function") {
        p.addStars(mastered && !wasMastered ? 3 : 1);
      }
    }
  } catch { /* silent — never break lesson flow */ }
  return next[code];
}

/** Manually reset one outcome (used by parents / debug). */
export function resetOutcome(code) {
  const all = readRaw();
  if (!all[code]) return;
  const { [code]: _dropped, ...rest } = all;
  writeRaw(rest);
}

/** True if the child has crossed the mastery bar for this outcome. */
export function isMastered(code) {
  return !!readRaw()[code]?.mastered;
}

/** Number of outcomes the child has mastered so far. */
export function masteredCount() {
  const all = readRaw();
  return Object.values(all).filter((r) => r.mastered).length;
}

/**
 * How many more passing attempts are needed to reach mastery.
 * Returns 0 once already mastered.
 */
export function passesRemaining(code) {
  const rec = getOutcomeMastery(code);
  if (rec.mastered) return 0;
  const passes = rec.attempts.filter((a) => a.score >= PASS_THRESHOLD).length;
  return Math.max(0, REQUIRED_PASSES - passes);
}

export const MASTERY_RULE = {
  passThreshold: PASS_THRESHOLD,
  requiredPasses: REQUIRED_PASSES,
  totalQuestions: 5,
};
